// src/contexts/AuthSessionProvider.tsx
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import type { ReactNode } from "react";
import { supabase } from "../supabase/client";
import type { Session } from "@supabase/supabase-js";
import { AuthSessionContext } from "./AuthSessionContext";

interface AuthSessionProviderProps {
  children: ReactNode;
}

export const AuthSessionProvider: React.FC<AuthSessionProviderProps> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true); // Start with loading true

  // Track which user has been ensured to prevent redundant calls
  const ensuredForUserIdRef = useRef<string | null>(null);

  const ensureUserRecord = async (userId: string, roleFromMetadata?: string) => {
    try {
      // Skip database check for CSR/Admin users
      if (roleFromMetadata === 'csr' || roleFromMetadata === 'admin') {
        console.log('CSR/Admin user detected - skipping user record check');
        return;
      }
      
      // Check if user already has a public.users record
      const { data: existingUser, error: checkError } = await supabase
        .from('users')
        .select('id, profile_completed')
        .eq('id', userId)
        .single();

      if (checkError && checkError.code === 'PGRST116') {
        // No record exists - auto-complete the profile
        console.log('New user detected - auto-completing profile...');

        try {
          const { data, error } = await supabase.functions.invoke('autoCompleteProfile');

          if (error) {
            console.error('Failed to auto-complete profile:', error);
          } else {
            console.log('Profile auto-completed successfully:', data);
          }
        } catch (autoCompleteError) {
          console.error('Error calling autoCompleteProfile:', autoCompleteError);
        }
        return;
      } else if (existingUser) {
        // Record already exists, nothing to do
        console.log('User record already exists');
      } else if (checkError) {
        // Handle other errors gracefully
        console.error('Error checking user record:', checkError);
      }
    } catch (error) {
      console.error('Error in ensureUserRecord:', error);
      // Don't throw - we want initialization to continue even if this fails
    }
  };

  const updateUserRole = useCallback((currentSession: Session | null) => {
    if (currentSession?.user) {
      const role = currentSession.user.user_metadata?.role || 'user';
      setUserRole(role);
      console.log("User role updated:", role, "Session metadata:", currentSession.user.user_metadata);
    } else {
      setUserRole(null);
    }
  }, []);

  const refreshSession = useCallback(async () => {
    const { data, error } = await supabase.auth.refreshSession();
    if (error) {
      console.error("Error refreshing session:", error);
    } else {
      setSession(data.session);
      updateUserRole(data.session);
    }
  }, [updateUserRole]);

  useEffect(() => {
    const init = async () => {
      console.log('🔐 AuthSessionProvider: Starting session initialization');
      setIsLoading(true);

      try {
        // Add timeout wrapper to prevent hanging on getSession()
        // Supabase API sometimes hangs - this prevents infinite loading
        const getSessionWithTimeout = () => {
          return Promise.race([
            supabase.auth.getSession(),
            new Promise<{ data: { session: Session | null } }>((_, reject) =>
              setTimeout(() => reject(new Error('getSession timeout after 3 seconds')), 3000)
            )
          ]);
        };

        let sessionData;
        try {
          sessionData = await getSessionWithTimeout();
          console.log('🔐 AuthSessionProvider: Got session:', {
            hasSession: !!sessionData.data.session,
            userEmail: sessionData.data.session?.user?.email
          });
        } catch (timeoutError) {
          console.warn('🔐 AuthSessionProvider: getSession timed out, using null session');
          sessionData = { data: { session: null } };
        }

        setSession(sessionData.data.session);
        updateUserRole(sessionData.data.session);

        // Ensure public.users record exists for authenticated user (initial load only)
        if (sessionData.data.session?.user?.id) {
          const roleFromMetadata = sessionData.data.session.user.user_metadata?.role;
          console.log('🔐 AuthSessionProvider: About to call ensureUserRecord');
          await ensureUserRecord(sessionData.data.session.user.id, roleFromMetadata);
          ensuredForUserIdRef.current = sessionData.data.session.user.id;
          console.log('🔐 AuthSessionProvider: ensureUserRecord completed');

          // Update last_active_at (non-blocking, fire-and-forget)
          supabase
            .from('users')
            .update({ last_active_at: new Date().toISOString() })
            .eq('id', sessionData.data.session.user.id)
            .then(() => console.log('Updated last_active_at'))
            .catch((err) => console.warn('Failed to update last_active_at:', err));
        }
      } catch (error) {
        console.error('🔐 AuthSessionProvider: Error during initialization:', error);
      } finally {
        console.log('🔐 AuthSessionProvider: Setting isLoading to false');
        setIsLoading(false);
      }
    };
    init();

    // Listen to auth state changes
    // IMPORTANT: Only run expensive ensureUserRecord() when user ID actually changes
    // This prevents the 3-second delay when tab regains visibility
    const { data } = supabase.auth.onAuthStateChange(async (_event, session) => {
      const uid = session?.user?.id ?? null;

      console.log('🔐 Auth state change:', { event: _event, userId: uid });

      // Always update session and role immediately (fast operations)
      setSession(session);
      updateUserRole(session);

      // Handle logout: reset ensured user tracking
      if (_event === 'SIGNED_OUT') {
        ensuredForUserIdRef.current = null;
        console.log('🔐 User signed out - reset ensured user tracking');
        return;
      }

      // Only run expensive ensureUserRecord() when user ID changes in this tab
      // This prevents redundant calls on visibility changes, token refreshes, etc.
      if (_event === 'SIGNED_IN' && uid && ensuredForUserIdRef.current !== uid) {
        const roleFromMetadata = session?.user?.user_metadata?.role;
        console.log('🔐 New user detected - running ensureUserRecord for:', uid);

        // Run in background - don't await (don't block login/navigation)
        ensureUserRecord(uid, roleFromMetadata).then(() => {
          console.log('🔐 ensureUserRecord completed in background');
        }).catch((error) => {
          console.error('🔐 ensureUserRecord failed in background:', error);
        });

        ensuredForUserIdRef.current = uid;

        // Update last_active_at on fresh sign-in (non-blocking)
        supabase
          .from('users')
          .update({ last_active_at: new Date().toISOString() })
          .eq('id', uid)
          .then(() => console.log('Updated last_active_at on sign-in'))
          .catch((err) => console.warn('Failed to update last_active_at:', err));
      } else if (_event === 'SIGNED_IN') {
        console.log('🔐 Tab visibility optimization: Skipped ensureUserRecord (user already ensured)');
      }

      // TOKEN_REFRESHED, USER_UPDATED, PASSWORD_RECOVERY: Fast-path only
      // Session and role updates happen above, no heavy operations needed
    });

    return () => {
      if (data?.subscription) {
        data.subscription.unsubscribe();
      }
    };
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => {
    return { session, setSession, refreshSession, userRole, isLoading };
  }, [session, userRole, isLoading, refreshSession]);

  return (
    <AuthSessionContext.Provider value={contextValue}>
      {children}
    </AuthSessionContext.Provider>
  );
};






